import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductsContext, CartContext } from '../App';

const ProductList = React.memo(({ products, category, sort }) => {
  const { searchQuery } = useContext(ProductsContext);
  const { addToCart } = useContext(CartContext);
  const [imageIndices, setImageIndices] = useState({});
  const [quantities, setQuantities] = useState({});

  let filtered = products
    .filter((p) => category === 'All' || p.category === category)
    .filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (sort === 'price-low') filtered.sort((a, b) => (a.price || Object.values(a.prices || {})[0] || 0) - (b.price || Object.values(b.prices || {})[0] || 0));
  if (sort === 'price-high') filtered.sort((a, b) => (b.price || Object.values(b.prices || {})[0] || 0) - (a.price || Object.values(a.prices || {})[0] || 0));
  if (sort === 'name') filtered.sort((a, b) => a.name.localeCompare(b.name));

  const handleImageHover = (productId) => {
    setImageIndices((prev) => {
      const product = products.find((p) => p.id === productId);
      const imageKeys = Array.isArray(product.images)
        ? product.images
        : Object.keys(product.images || {});
      const currentIndex = prev[productId] || 0;
      const nextIndex = (currentIndex + 1) % imageKeys.length;
      return { ...prev, [productId]: nextIndex };
    });
  };

  const handleMouseEnter = (productId) => {
    const interval = setInterval(() => {
      handleImageHover(productId);
    }, 800);

    document.querySelector(`[data-product-id="${productId}"]`)?.setAttribute('data-interval', interval);
  };

  const handleMouseLeave = (productId) => {
    const interval = document.querySelector(`[data-product-id="${productId}"]`)?.getAttribute('data-interval');
    if (interval) {
      clearInterval(parseInt(interval));
      document.querySelector(`[data-product-id="${productId}"]`)?.removeAttribute('data-interval');
    }
    setImageIndices((prev) => ({ ...prev, [productId]: 0 }));
  };

  const getQuantity = (productId) => quantities[productId] || 1;

  const setQuantity = (productId, value) => {
    const numValue = Math.max(1, parseInt(value, 10) || 1);
    setQuantities((prev) => ({ ...prev, [productId]: numValue }));
  };

  const handleAddToCart = (product) => {
    const qty = getQuantity(product.id);
    if (qty > 0) {
      const itemToAdd = { ...product, price: getDisplayPrice(product) }; // <-- ensure price
      for (let i = 0; i < qty; i++) {
        addToCart(itemToAdd);
      }
      setQuantities((prev) => ({ ...prev, [product.id]: 1 }));
    }
  };

  // Helper function to get display price
  const getDisplayPrice = (product) => {
    if (product.price !== undefined) {
      return product.price;
    }
    if (product.prices && typeof product.prices === 'object') {
      const firstPrice = Object.values(product.prices)[0];
      return firstPrice || 0;
    }
    return 0;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }} className="product-list">
      {filtered.map((product) => {
        const currentImageIndex = imageIndices[product.id] || 0;

        // Handle both old array and new object format for images
        let imageKeys = [];
        if (Array.isArray(product.images)) {
          imageKeys = product.images;
        } else if (typeof product.images === 'object' && product.images !== null) {
          imageKeys = Object.values(product.images);
        }

        const totalImages = imageKeys.length || 1;
        const currentImage = imageKeys[currentImageIndex % totalImages] || 'https://via.placeholder.com/300x300?text=No+Image';

        const quantity = getQuantity(product.id);
        const displayPrice = getDisplayPrice(product);

        return (
          <div
            key={product.id}
            className="product-card"
            data-product-id={product.id}
            onMouseEnter={() => handleMouseEnter(product.id)}
            onMouseLeave={() => handleMouseLeave(product.id)}
            data-aos="fade-in"
          >
            <div className="product-image-container-updated">
              <div className="product-image-wrapper">
                <img
                  src={currentImage}
                  alt={product.name}
                  className="product-image"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x300?text=Product+Image';
                  }}
                />
              </div>
              {totalImages > 1 && (
                <span className="image-indicator">
                  {currentImageIndex + 1}/{totalImages}
                </span>
              )}
            </div>

            <div className="product-card-content">
              <h4>{product.name}</h4>
              <p className="product-price">${displayPrice.toFixed(2)}</p>

              {/* Quantity Selector */}
              <div className="quantity-selector">
                <button 
                  className="qty-btn qty-minus"
                  onClick={() => setQuantity(product.id, quantity - 1)}
                >
                  −
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(product.id, e.target.value)}
                  min="1"               // was 0 -> changed to 1
                  className="qty-input"
                />
                <button 
                  className="qty-btn qty-plus"
                  onClick={() => setQuantity(product.id, quantity + 1)}
                >
                  +
                </button>
              </div>

              {/* Action Buttons */}
              <div className="product-card-actions">
                <button 
                  className="add-to-cart-btn"
                  onClick={() => handleAddToCart(product)}
                  disabled={quantity === 0}
                >
                  Add to Cart
                </button>
                <Link to={`/product/${product.id}`} className="view-details-link">
                  View Details
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default ProductList;